import { useCallback, useEffect, useState } from 'react';
import { useTranslator } from 'tradera/lang/translate';
import { InternationalIdentificationModal } from './international-identification-modal';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { closeModal, closeNewMessageModal } from 'tradera/state/ui/reducer';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { useLocation } from 'tradera/hooks/use-location';
import { showSuccessToast } from 'tradera/services/toasts';
import { setMemberActive } from 'tradera/state/member/reducer';
import { setIsInternationalWacko } from 'tradera/state/views/view-item/reducer';
import type { TFunction } from 'i18next';
import { pollSessionStatus } from 'tradera/components/international-identification-modal/poll-session-status';

export const headerInternationalIdentificationModalId =
    'headerInternationalIdentificationModalId';

export const HeaderInternationalIdentificationModal = () => {
    const { t } = useTranslator();
    const dispatch = useAppDispatch();
    const { currentModal, currentModalData } = useAppSelector(
        (state) => state.ui.modals
    );
    const purposeQueryParam = new URLSearchParams(useLocation().search).get(
        'purpose'
    );
    const purpose =
        currentModalData?.purpose || purposeQueryParam || 'Identification';

    const [errors, setErrors] = useState<string[]>([]);

    const clearModal = useCallback(() => {
        setErrors([]);
        setIsResponseLoading(false);
    }, []);

    const onVerifyComplete = useCallback(
        (shouldSetMemberActive?: boolean) => {
            showSuccessToast('international_identification_success');
            setIsResponseLoading(false);
            dispatch(closeModal());
            clearModal();
            if (shouldSetMemberActive) {
                dispatch(setMemberActive());
            }
            if (purpose === 'Payment') {
                dispatch(setIsInternationalWacko(false));
            }
        },
        [clearModal, dispatch, purpose]
    );

    const onCloseModal = useCallback(() => {
        dispatch(closeModal());
        dispatch(closeNewMessageModal());
        GtmService.trackAction(
            purpose,
            'International Verification',
            'Close modal'
        );
        clearModal();
    }, [clearModal, dispatch, purpose]);

    const [isResponseLoading, setIsResponseLoading] = useState(false);
    const identifiedQueryParam =
        new URLSearchParams(useLocation().search).get('identified') || '';

    useEffect(() => {
        let isStale = false;
        if (purposeQueryParam !== purpose) {
            return;
        }

        const success = (shouldSetMemberActive: boolean) => () => {
            if (isStale) {
                return;
            }
            GtmService.trackAction(identifiedQueryParam, 'Success', '');
            onVerifyComplete(shouldSetMemberActive);
        };
        const error = (errors: string[]) => {
            if (isStale) {
                return;
            }

            setIsResponseLoading(false);

            setErrors(errors);
            errors.forEach((error) => {
                GtmService.trackAction(identifiedQueryParam, 'Error', error);
            });
        };

        let cleanup = () => {};
        if (identifiedQueryParam === 'mitid') {
            setIsResponseLoading(true);
            cleanup = pollSessionStatus(
                success(true),
                error,
                'MitIdSessionStatus'
            );
        } else if (identifiedQueryParam === 'paypal') {
            setIsResponseLoading(true);
            cleanup = pollSessionStatus(
                success(false),
                error,
                'PayPalSessionStatus'
            );
        } else if (identifiedQueryParam === 'idin') {
            setIsResponseLoading(true);
            cleanup = pollSessionStatus(
                success(true),
                error,
                'IdinSessionStatus'
            );
        }

        return () => {
            // eslint-disable-next-line better-mutation/no-mutation
            isStale = true;
            cleanup();
        };
    }, [
        t,
        identifiedQueryParam,
        purposeQueryParam,
        dispatch,
        purpose,
        onVerifyComplete
    ]);

    const errorText = getErrorText(t, errors, identifiedQueryParam);

    return currentModal === headerInternationalIdentificationModalId ||
        isResponseLoading ||
        errorText ? (
        <InternationalIdentificationModal
            onTop
            onCloseModal={onCloseModal}
            onVerifyComplete={onVerifyComplete}
            purpose={purpose}
            introText={t('international_identification_intro_text')}
            heading={t('international_identification_heading')}
            errorText={errorText}
            isResponseLoading={isResponseLoading}
        />
    ) : null;
};

const getErrorText = (
    t: TFunction,
    errors: string[],
    identifiedQueryParam: string
): string | null => {
    if (errors.length === 0) {
        return null;
    }

    const method =
        identifiedQueryParam === 'idin'
            ? 'iDIN'
            : identifiedQueryParam === 'paypal'
              ? 'Paypal'
              : 'MitId';

    switch (true) {
        case errors.includes('NAME_HIDDEN'):
            return t('international_identification_validation_name_hidden');
        case errors.includes('ACCOUNT_ALREADY_IN_USE'):
        case errors.includes('DOCUMENT_IN_USE'):
            return t(
                'international_identification_validation_document_in_use',
                { method }
            );
        case errors.includes('COUNTRY'):
        case errors.includes('DATE_OF_BIRTH'):
        case errors.includes('FIRST_NAME'):
        case errors.includes('LAST_NAME'):
            return t(
                'international_identification_validation_account_details',
                { method }
            );
        case errors.includes('ErrorUnavailable'):
            return t('international_identification_errorUnavailable');
        case errors.includes('ACCOUNT_NOT_VERIFIED'):
        default:
            return t('international_identification_contactCustomerSupportText');
    }
};
